"use client";
/**
 * iPhone bottom tabs — the five destinations you can reach with a thumb.
 * "More" opens the same drawer as the header hamburger. Hidden on tablet
 * and desktop (`mpp-phone-only`).
 */
import { useLayoutEffect, type ReactNode } from "react";
import Link from "@/lib/spa/Link";
import { hrefOf, type NavItem } from "@/lib/nav/navConfig";
import { COMPACT_TAB_HREFS, useCompactViewport } from "@/lib/display/compactViewport";

type TabDef = { href: string; label: string; icon: ReactNode };

const ICONS: Record<string, ReactNode> = {
  "/flow": (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h16M4 12h10M4 18h7" />
    </svg>
  ),
  "/dashboard": (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  "/scanner": (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  ),
  "/charts": (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19V5M4 19h16" />
      <path d="M8 15l3.5-4.5 3 2.5L19 7" />
    </svg>
  ),
};

function matches(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function MobileTabBar({
  pathname,
  items,
  onMore,
}: {
  pathname: string;
  items: NavItem[];
  onMore: () => void;
}) {
  const byHref = new Map<string, NavItem>();
  const walk = (list: NavItem[]) => {
    for (const it of list) {
      const href = hrefOf(it);
      if (href) byHref.set(href, it);
      if (it.children) walk(it.children);
    }
  };
  walk(items);

  const tabs: TabDef[] = [];
  for (const href of COMPACT_TAB_HREFS) {
    const it = byHref.get(href);
    if (!it || !it.visible) continue;
    tabs.push({ href, label: it.label, icon: ICONS[href] ?? ICONS["/dashboard"] });
  }

  const onPrimary = tabs.some((t) => matches(pathname, t.href));
  const { phone } = useCompactViewport();

  useLayoutEffect(() => {
    if (!phone) {
      document.documentElement.classList.remove("mpp-has-tabbar");
      return;
    }
    document.documentElement.classList.add("mpp-has-tabbar");
    return () => document.documentElement.classList.remove("mpp-has-tabbar");
  }, [phone]);

  return (
    <nav
      className="mpp-phone-only fixed inset-x-0 bottom-0 z-[70] border-t border-line bg-surface/95 shadow-toolbar backdrop-blur"
      style={{ paddingBottom: "var(--mpp-sab)", paddingLeft: "var(--mpp-sal)", paddingRight: "var(--mpp-sar)" }}
      aria-label="Primary"
    >
      <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((t) => {
          const active = matches(pathname, t.href);
          return (
            <Link
              key={t.href}
              href={t.href}
              prefetch
              aria-current={active ? "page" : undefined}
              className={`flex min-h-[52px] flex-col items-center justify-center gap-0.5 px-1 text-2xs font-semibold ${
                active ? "text-accent" : "text-ink-muted"
              }`}
            >
              {t.icon}
              <span className="max-w-full truncate">{t.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onMore}
          aria-label="More pages"
          className={`flex min-h-[52px] flex-col items-center justify-center gap-0.5 px-1 text-2xs font-semibold ${
            onPrimary ? "text-ink-muted" : "text-accent-ink"
          }`}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="6" cy="12" r="1.4" fill="currentColor" />
            <circle cx="12" cy="12" r="1.4" fill="currentColor" />
            <circle cx="18" cy="12" r="1.4" fill="currentColor" />
          </svg>
          <span>More</span>
        </button>
      </div>
    </nav>
  );
}
